// ─── Configuration ───────────────────────────────────────────────────────────

const EMOJIS = ['💃', '💃', '💃', '🌶️', '💞', '🍸', '🔬', '⚛️'];

const BASE_SPEED_PX_S = 100;   // base travel speed (px/s)
const SPEED_VARIANCE = 0.20;  // ±20% per emoji (range 0.8×–1.2×)
const EMOJI_SIZE_PX = 30;    // approximate rendered emoji size for density formula
const COVERAGE_TARGET = 0.20;  // steady-state fraction of viewport covered by emojis
const WIGGLE_PERIOD_MS = 500;   // one full wiggle cycle duration; always constant

const BATCH_INTERVAL_MS = 1000; // normal spawnBatch interval (ms)
const EXPLOSION_BATCH_INTERVAL_MS = 100;  // faster interval during explosion

// Timeline (seconds from song start)
const T_RAMP_START = 18;   // wiggle ON + linear ramp begins
const T_RAMP_END = 46;   // ramp completes; first explosion starts immediately
const T_EXPLOSION1_END = 75;   // first explosion ends; rate drops to ramp-peak
const T_EXPLOSION2_START = 106;  // second explosion starts (1:46)
const T_EXPLOSION2_END = 130;  // second explosion ends (2:10); rate drops to initial
const T_SONG_END = 152;  // song ends (2:32); restart cycle

const RAMP_MULTIPLIER = 2.0;  // rate at ramp-peak = initialRate × RAMP_MULTIPLIER
const EXPLOSION_MULT = 10;   // during explosion, rate = rampPeak × EXPLOSION_MULT
const STOP_SPEED_MULT = 25;   // updatePlaybackRate factor when celebration stops

// Safety cap: prevents DOM overload during explosion
const MAX_LIVE_EMOJIS = 2500;

// ─── Module state ────────────────────────────────────────────────────────────

let active = false;
let initRateLR = 0;
let initRateTB = 0;
let currentRateLR = 0;
let currentRateTB = 0;
let currentBatchInterval = BATCH_INTERVAL_MS;
let explosionActive = false;
let celebLayer: HTMLElement | null = null;
let audio: HTMLAudioElement | null = null;
let batchTimer: ReturnType<typeof setTimeout> | null = null;
const timers: ReturnType<typeof setTimeout>[] = [];
const animMap = new Map<HTMLElement, Animation>();

// ─── Utilities ───────────────────────────────────────────────────────────────

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}

function rand(lo: number, hi: number): number {
  return lo + Math.random() * (hi - lo);
}

// Compute per-direction initial spawn rates so that at steady state the
// viewport is COVERAGE_TARGET covered by emojis.
// Derivation: at steady state, LR emojis in flight = rateLR × crossTime.
//   rateLR × (W / speed) × emojiArea = 0.5 × COVERAGE_TARGET × W × H
//   → rateLR = 0.5 × COVERAGE_TARGET × H × speed / emojiArea  (W cancels)
// Similarly for TB with W and H swapped.
function computeInitialRates(): { lr: number; tb: number } {
  const W = window.innerWidth;
  const H = window.innerHeight;
  const area = EMOJI_SIZE_PX * EMOJI_SIZE_PX;
  return {
    lr: 0.5 * COVERAGE_TARGET * H * BASE_SPEED_PX_S / area,
    tb: 0.5 * COVERAGE_TARGET * W * BASE_SPEED_PX_S / area,
  };
}

// ─── Spawning ────────────────────────────────────────────────────────────────

function spawnEmoji(dir: 'lr' | 'tb', delayMs: number, speedMult = 1): void {
  if (!celebLayer || animMap.size >= MAX_LIVE_EMOJIS) return;

  const W = window.innerWidth;
  const H = window.innerHeight;
  const sz = EMOJI_SIZE_PX;

  const wrapper = document.createElement('div');
  wrapper.className = 'emoji-wrapper';

  const inner = document.createElement('span');
  inner.className = 'emoji-inner ' + (Math.random() < 0.5 ? 'wl' : 'wr');
  inner.textContent = pick(EMOJIS);
  wrapper.appendChild(inner);
  celebLayer.appendChild(wrapper);

  const speed = BASE_SPEED_PX_S * speedMult * (1 + rand(-SPEED_VARIANCE, SPEED_VARIANCE));
  let dist: number;
  let keyframes: Keyframe[];

  if (dir === 'lr') {
    const goRight = Math.random() < 0.5;
    const y = rand(0, H);
    dist = W + 2 * sz;
    if (goRight) {
      wrapper.style.cssText = `left:${-sz}px;top:${y}px`;
      keyframes = [{ transform: 'translateX(0)' }, { transform: `translateX(${dist}px)` }];
    } else {
      wrapper.style.cssText = `left:${W + sz}px;top:${y}px`;
      keyframes = [{ transform: 'translateX(0)' }, { transform: `translateX(${-dist}px)` }];
    }
  } else {
    const goDown = Math.random() < 0.5;
    const x = rand(0, W);
    dist = H + 2 * sz;
    if (goDown) {
      wrapper.style.cssText = `top:${-sz}px;left:${x}px`;
      keyframes = [{ transform: 'translateY(0)' }, { transform: `translateY(${dist}px)` }];
    } else {
      wrapper.style.cssText = `top:${H + sz}px;left:${x}px`;
      keyframes = [{ transform: 'translateY(0)' }, { transform: `translateY(${-dist}px)` }];
    }
  }

  const dur = (dist / speed) * 1000;
  const anim = wrapper.animate(keyframes, {
    duration: dur,
    delay: delayMs,
    fill: 'forwards',
    easing: 'linear',
  });

  animMap.set(wrapper, anim);
  anim.onfinish = () => {
    wrapper.remove();
    animMap.delete(wrapper);
  };
}

function spawnBatch(): void {
  if (!active) return;
  const interval = currentBatchInterval;
  const lrCount = Math.max(1, Math.ceil(currentRateLR * interval / 1000));
  const tbCount = Math.max(1, Math.ceil(currentRateTB * interval / 1000));
  const total = lrCount + tbCount;

  // Interleave LR and TB proportionally so neither direction is starved when the
  // live-emoji cap kicks in. Spawn whichever direction is proportionally furthest behind.
  const speedMult = explosionActive ? 2 : 1;
  let lr = 0, tb = 0;
  for (let i = 0; i < total; i++) {
    const delay = (i / total) * interval;
    const pickLR = lr < lrCount && (tb >= tbCount || (lr / lrCount) <= (tb / tbCount));
    if (pickLR) { spawnEmoji('lr', delay, speedMult); lr++; }
    else { spawnEmoji('tb', delay, speedMult); tb++; }
  }

  batchTimer = setTimeout(spawnBatch, interval);
}

// ─── Timeline ────────────────────────────────────────────────────────────────

function restartCycle(): void {
  if (!active) return;
  timers.splice(0).forEach(id => clearTimeout(id));
  explosionActive = false;
  currentRateLR = initRateLR;
  currentRateTB = initRateTB;
  currentBatchInterval = BATCH_INTERVAL_MS;
  celebLayer?.classList.remove('wiggling');
  if (audio) { audio.currentTime = 0; audio.play().catch(() => { }); }
  scheduleTimeline(initRateLR, initRateTB);
}

function scheduleTimeline(lr: number, tb: number): void {
  const rampSteps = T_RAMP_END - T_RAMP_START;
  const peakLR = lr * RAMP_MULTIPLIER;
  const peakTB = tb * RAMP_MULTIPLIER;

  // Wiggle + linear ramp start together
  timers.push(setTimeout(() => {
    celebLayer?.classList.add('wiggling');
  }, T_RAMP_START * 1000));

  for (let s = 0; s < rampSteps; s++) {
    const step = s;
    timers.push(setTimeout(() => {
      const p = step / rampSteps;
      currentRateLR = lr + (peakLR - lr) * p;
      currentRateTB = tb + (peakTB - tb) * p;
    }, (T_RAMP_START + step) * 1000));
  }

  // First explosion immediately after ramp ends
  timers.push(setTimeout(() => {
    explosionActive = true;
    currentRateLR = peakLR * EXPLOSION_MULT;
    currentRateTB = peakTB * EXPLOSION_MULT;
    currentBatchInterval = EXPLOSION_BATCH_INTERVAL_MS;
  }, T_RAMP_END * 1000));

  // First explosion ends — settle at ramp-peak
  timers.push(setTimeout(() => {
    explosionActive = false;
    currentRateLR = peakLR;
    currentRateTB = peakTB;
    currentBatchInterval = BATCH_INTERVAL_MS;
  }, T_EXPLOSION1_END * 1000));

  // Second explosion at 1:46
  timers.push(setTimeout(() => {
    explosionActive = true;
    currentRateLR = peakLR * EXPLOSION_MULT;
    currentRateTB = peakTB * EXPLOSION_MULT;
    currentBatchInterval = EXPLOSION_BATCH_INTERVAL_MS;
  }, T_EXPLOSION2_START * 1000));

  // Second explosion ends at 2:10 — drop back to initial rate
  timers.push(setTimeout(() => {
    explosionActive = false;
    currentRateLR = initRateLR;
    currentRateTB = initRateTB;
    currentBatchInterval = BATCH_INTERVAL_MS;
  }, T_EXPLOSION2_END * 1000));

  // Song ends at 2:32 — restart the whole cycle
  timers.push(setTimeout(restartCycle, T_SONG_END * 1000));
}

// ─── Public API ──────────────────────────────────────────────────────────────

export function startCelebration(layer: HTMLElement): void {
  if (active) stopCelebration();

  active = true;
  currentBatchInterval = BATCH_INTERVAL_MS;
  celebLayer = layer;
  celebLayer.classList.remove('wiggling');
  celebLayer.style.setProperty('--wiggle-dur', `${WIGGLE_PERIOD_MS}ms`);

  const { lr, tb } = computeInitialRates();
  initRateLR = lr;
  initRateTB = tb;
  currentRateLR = lr;
  currentRateTB = tb;

  audio = new Audio('audio/music.mp3');
  audio.play().catch(() => { });

  scheduleTimeline(lr, tb);
  spawnBatch();
}

export function stopCelebration(): void {
  if (!active) return;
  active = false;

  timers.splice(0).forEach(id => clearTimeout(id));
  if (batchTimer !== null) { clearTimeout(batchTimer); batchTimer = null; }

  audio?.pause();
  audio = null;

  // Speed up in-progress emojis; cancel those still in their stagger delay
  for (const [wrapper, anim] of [...animMap]) {
    const ct = Number(anim.currentTime);
    if (!isNaN(ct) && ct > 0) {
      try { anim.updatePlaybackRate(STOP_SPEED_MULT); } catch { /* already finished */ }
    } else {
      anim.cancel();
      wrapper.remove();
      animMap.delete(wrapper);
    }
  }

  celebLayer?.classList.remove('wiggling');
}
